import { useEffect, useMemo, useState } from "react";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  Search,
} from "lucide-react";

/**
 * DataTable — one reusable, dependency-free table for the SAARTHI admin
 * portal. Handles sort / search / paginate / page-size / sticky header /
 * loading & empty states / CSV + Excel export. Zero data fetching — pages
 * pass `rows` and `columns`, everything else is client-side.
 *
 * Props:
 *   columns      Array<{
 *                  key:        string      // unique id (matches row field for auto-accessor)
 *                  label:      string      // header text
 *                  accessor?:  (row) => any  // optional value getter (defaults to row[key])
 *                  render?:    (row) => ReactNode  // optional cell renderer (defaults to accessor)
 *                  sortable?:  boolean     // default true
 *                  searchable?: boolean    // default true — included in global search
 *                  exportable?: boolean    // default true — included in CSV/Excel export
 *                  className?: string      // per-cell classes (td)
 *                  headerClassName?: string
 *                  align?:     "left" | "right" | "center"
 *                }>
 *   rows         Array<Record>
 *   getRowId     (row) => string          // stable key for React
 *   loading      boolean
 *   error        string
 *   emptyMessage string
 *   toolbar      ReactNode | ReactNode[]  // extra filters rendered next to search
 *   actions      (row) => ReactNode       // "Actions" column renderer (kept outside sort/search)
 *   defaultSort  { key: string, dir: "asc" | "desc" }
 *   pageSize     number (default 25)
 *   pageSizeOptions Array<number> (default [10, 25, 50, 100])
 *   exportFilename string (base name for CSV / Excel, without extension)
 *   testid       string  // base id, used to derive child testids
 */
export default function DataTable({
  columns,
  rows,
  getRowId = (row, i) => row?.id ?? i,
  loading = false,
  error = "",
  emptyMessage = "No records found.",
  toolbar = null,
  actions = null,
  defaultSort = null,
  pageSize: initialPageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  exportFilename = "export",
  testid = "datatable",
}) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(defaultSort?.key || null);
  const [sortDir, setSortDir] = useState(defaultSort?.dir || "asc");
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);

  // ------------------------------------------------------------------
  // Global search — across all searchable columns
  // ------------------------------------------------------------------
  const searchableCols = useMemo(
    () => columns.filter((c) => c.searchable !== false),
    [columns]
  );

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      searchableCols.some((c) => {
        const v = c.accessor ? c.accessor(row) : row[c.key];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [rows, searchableCols, search]);

  // ------------------------------------------------------------------
  // Sorting
  // ------------------------------------------------------------------
  const sorted = useMemo(() => {
    if (!sortKey) return searched;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return searched;
    const acc = col.accessor || ((row) => row[col.key]);
    const dir = sortDir === "desc" ? -1 : 1;
    const arr = [...searched];
    arr.sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      // undefined / null go last regardless of direction.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number")
        return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, {
        numeric: true,
        sensitivity: "base",
      }) * dir;
    });
    return arr;
  }, [searched, sortKey, sortDir, columns]);

  // ------------------------------------------------------------------
  // Pagination
  // ------------------------------------------------------------------
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // If filters shrink the dataset, keep the page valid.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);
  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize]
  );

  // ------------------------------------------------------------------
  // Export helpers (currently-filtered dataset, all pages)
  // ------------------------------------------------------------------
  const exportCols = useMemo(
    () => columns.filter((c) => c.exportable !== false),
    [columns]
  );

  function buildRowsForExport() {
    return sorted.map((row) => {
      const out = {};
      for (const c of exportCols) {
        const v = c.accessor ? c.accessor(row) : row[c.key];
        out[c.label] = v == null ? "" : String(v);
      }
      return out;
    });
  }

  function downloadBlob(content, mime, filename) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function exportCsv() {
    const data = buildRowsForExport();
    const headers = exportCols.map((c) => c.label);
    const esc = (s) => {
      const str = String(s ?? "");
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [headers.map(esc).join(",")];
    for (const r of data) lines.push(headers.map((h) => esc(r[h])).join(","));
    // UTF-8 BOM so Excel opens Devanagari / diacritics correctly.
    downloadBlob("\uFEFF" + lines.join("\r\n"), "text/csv;charset=utf-8", `${exportFilename}.csv`);
  }

  function exportExcel() {
    // SpreadsheetML 2003 — a single XML file that Excel opens natively as
    // a .xls workbook. Zero third-party dependency, tiny footprint.
    const data = buildRowsForExport();
    const headers = exportCols.map((c) => c.label);
    const xmlEsc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    const headerRow =
      "<Row>" +
      headers
        .map(
          (h) =>
            `<Cell><Data ss:Type="String">${xmlEsc(h)}</Data></Cell>`
        )
        .join("") +
      "</Row>";
    const dataRows = data
      .map(
        (r) =>
          "<Row>" +
          headers
            .map((h) => {
              const v = r[h];
              const isNumber =
                v !== "" && !isNaN(v) && !isNaN(parseFloat(v));
              return `<Cell><Data ss:Type="${
                isNumber ? "Number" : "String"
              }">${xmlEsc(v)}</Data></Cell>`;
            })
            .join("") +
          "</Row>"
      )
      .join("");
    const xml =
      '<?xml version="1.0"?>' +
      '<?mso-application progid="Excel.Sheet"?>' +
      '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" ' +
      'xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">' +
      '<Worksheet ss:Name="Data"><Table>' +
      headerRow +
      dataRows +
      "</Table></Worksheet></Workbook>";
    downloadBlob(
      xml,
      "application/vnd.ms-excel;charset=utf-8",
      `${exportFilename}.xls`
    );
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  function toggleSort(col) {
    if (col.sortable === false) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir("asc");
    }
  }

  const alignClass = (a) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  return (
    <section
      className="bg-white border border-slate-200 rounded-lg"
      data-testid={testid}
    >
      {/* Toolbar */}
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search…"
            data-testid={`${testid}-search`}
            className="pl-7 pr-3 py-1.5 rounded-md border border-slate-300 text-sm w-56"
          />
        </div>

        {toolbar}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={exportCsv}
            disabled={total === 0}
            data-testid={`${testid}-export-csv`}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
          <button
            type="button"
            onClick={exportExcel}
            disabled={total === 0}
            data-testid={`${testid}-export-excel`}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            <Download className="h-3 w-3" /> Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <p className="px-5 py-8 text-sm text-slate-500">Loading…</p>
        ) : error ? (
          <p className="px-5 py-8 text-sm text-red-600" data-testid={`${testid}-error`}>
            {error}
          </p>
        ) : total === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500" data-testid={`${testid}-empty`}>
            {emptyMessage}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid={`${testid}-table`}>
            <thead className="bg-slate-50 text-left text-slate-600 sticky top-0 z-10">
              <tr>
                {columns.map((c) => {
                  const isSorted = sortKey === c.key;
                  const isSortable = c.sortable !== false;
                  return (
                    <th
                      key={c.key}
                      onClick={() => toggleSort(c)}
                      data-testid={`${testid}-th-${c.key}`}
                      className={
                        "px-4 py-2 font-medium select-none " +
                        alignClass(c.align) +
                        " " +
                        (isSortable ? "cursor-pointer hover:text-slate-900" : "") +
                        " " +
                        (c.headerClassName || "")
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {isSortable ? (
                          isSorted ? (
                            sortDir === "asc" ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-40" />
                          )
                        ) : null}
                      </span>
                    </th>
                  );
                })}
                {actions ? (
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {paged.map((row, i) => {
                const rid = getRowId(row, i);
                return (
                  <tr
                    key={rid}
                    className="border-t border-slate-100 hover:bg-slate-50/60"
                    data-testid={`${testid}-row-${rid}`}
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className={
                          "px-4 py-2 " +
                          alignClass(c.align) +
                          " " +
                          (c.className || "text-slate-800")
                        }
                      >
                        {c.render
                          ? c.render(row)
                          : c.accessor
                            ? c.accessor(row)
                            : row[c.key] ?? "—"}
                      </td>
                    ))}
                    {actions ? (
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {actions(row)}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer / pagination */}
      {!loading && !error && total > 0 ? (
        <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center gap-3 text-xs text-slate-600">
          <span data-testid={`${testid}-count`}>
            Showing {startIdx}–{endIdx} of {total} records
          </span>
          <span className="mx-2 hidden sm:inline">·</span>
          <label className="inline-flex items-center gap-2">
            Rows
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              data-testid={`${testid}-page-size`}
              className="rounded-md border border-slate-300 px-2 py-1 bg-white"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>

          <div className="ml-auto inline-flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid={`${testid}-prev`}
              className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span data-testid={`${testid}-page`}>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              data-testid={`${testid}-next`}
              className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
